/** @format */

import LoginIcon from "@mui/icons-material/Login";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, TextField } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
// Import the SVG as a React component
import { ReactComponent as ThuthukaLogo } from "../../../assets/images/thuthukaLogoVector.svg";
import OtpVerificationProcess from "../../../common/custom/Verification/OtpVerificationProcess";
import MessageResponse from "../../../common/feedback/messageResponse";
import { sessions } from "../../../common/localstorage/SessionStorage";
import { GetMessageReponse } from "../../../common/Logic/ServerResponse";
import {
  FeedbackDto,
  Validation,
} from "../../../common/models/requests/FeedbackDto";
import {
  GetOtpThunk,
  VerifyOtpThunk,
  loginThunk,
  selectLogin,
} from "../loginSlice";
import { DecodeToken, SendVerificationCode, loginDto } from "../models";

export default function LoginForm() {
  const dispatch = useAppDispatch();
  const validationSchema = yup.object({});
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [isEmail, setIsEmail] = useState(false);
  const [isIdNumber, setIsIdNumber] = useState(false);
  const [expiryDate, setExpiryDate] = useState<number | null>(null);
  const navigate = useNavigate();
  const loginSelect = useAppSelector(selectLogin);

  const setLoginPath = (token: string) => {
    const decoded = jwtDecode<DecodeToken>(token);
    if (decoded.roles === "Admin") navigate("/dashboard");
    if (decoded.roles === "Applicant") navigate("/applicant");
    if (!decoded.roles) {
      sessions.removeToken();
      alert("No sufficient information");
    }
  };

  const validationSchemaSendVerification = yup.object({});

  const formik: FormikProps<loginDto> = useFormik<loginDto>({
    initialValues: {
      password: "",
      username: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values: loginDto) => {
      dispatch(loginThunk(values)).then((resp: any) => {
        if (
          (resp.type.includes("fulfilled") &&
            resp.payload?.validation.code === 201) ||
          (resp.type.includes("fulfilled") &&
            resp.payload?.validation.code === 200)
        ) {
          sessions.setToken(resp.payload?.data.token);
          setLoginPath(resp.payload?.data.token);
          window.location.reload();
        }
      });
    },
  });

  const formikSendCode: FormikProps<SendVerificationCode> =
    useFormik<SendVerificationCode>({
      initialValues: {
        otp: "",
        identityNumber: "",
      },
      validationSchema: validationSchemaSendVerification,
      onSubmit: (values: SendVerificationCode) => {
        values.identityNumber = formik.values.username;
        dispatch(VerifyOtpThunk(values)).then((data) => {
          const results = data.payload;
          if (!results || !results.data) {
            const feed = GetMessageReponse(
              {
                code: 400,
                messages: ["We could not validate the OTP"],
              } as Validation,
              false,
              data.type
            );
            setFeedback(feed);
            return;
          }

          if (results && results.data) {
            sessions.setToken(results.data.token);
            window.location.reload();
          }
        });
      },
    });

  useEffect(() => {
    const token = sessions.getToken();
    if (token) {
      setLoginPath(token);
    }
  }, []);

  React.useEffect(() => {
    if (
      loginSelect.loginState.status === "failed" &&
      loginSelect.loginState.error
    ) {
      const feed = GetMessageReponse(
        loginSelect.loginState.error,
        false,
        "fulfilled"
      );
      setFeedback(feed);
      return;
    }
  }, [loginSelect.loginState.status]);

  useEffect(() => {
    const resp = loginSelect.loginState.isCodeVerified;
    if (resp === true) {
      dispatch(loginThunk(formik.values)).then((resp: any) => {
        if (
          loginSelect.loginState.status === "failed" ||
          resp.payload?.errors
        ) {
          const feed = GetMessageReponse(
            loginSelect.loginState.error,
            false,
            resp.type
          );
          setFeedback(feed);
          return;
        }
        if (
          (resp.type.includes("fulfilled") &&
            resp.payload?.validation.code === 201) ||
          (resp.type.includes("fulfilled") &&
            resp.payload?.validation.code === 200)
        ) {
          sessions.setToken(resp.payload.data.token);
          setLoginPath(resp.payload.data.token);
          window.location.reload();
        }
      });
    }
  }, [loginSelect.loginState.isCodeVerified]);

  const ValidateEmail = () => {
    if (formik.values.username === "") return false;
    if (
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
        formik.values.username
      )
    ) {
      return true;
    }
    return false;
  };

  const isValidIdNumber = (): boolean => {
    if (formik.values.username === "") return false;
    const idRegex =
      /(([0-9]{2})(0|1)([0-9])([0-3])([0-9]))([ ]?)(([0-9]{4})([ ]?)([0-1][8]([ ]?)[0-9]))/gm;
    return idRegex.test(formik.values.username);
  };
  useEffect(() => {
    setIsEmail(ValidateEmail());
    setIsIdNumber(isValidIdNumber());
  }, [formik.values.username]);

  const handleLoginButton = () => {
    if (isEmail) {
      formik.submitForm();
      return;
    }

    if (isIdNumber && !expiryDate) {
      dispatch(GetOtpThunk(formik.values.username)).then((resp) => {
        if (
          loginSelect.loginState.status === "failed" ||
          resp.payload?.errors
        ) {
          const feed = GetMessageReponse(
            loginSelect.loginState.error,
            false,
            resp.type
          );
          setFeedback(feed);
          return;
        }
      });
    }
  };

  useEffect(() => {
    if (loginSelect.loginState && loginSelect.loginState.expiryDate) {
      setExpiryDate(loginSelect.loginState.expiryDate);
    }
  }, [loginSelect.loginState.expiryDate]);

  return (
    <Box className="form__container">
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          mb: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "200px",
            overflow: "hidden",
          }}
        >
          <ThuthukaLogo 
            className="thuthuka_login_image"
            style={{ width: '100%', height: 'auto' }}
            title="Thuthuka logo"
          />
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <MessageResponse feedback={feedback}  />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="username"
            name="username"
            label="Username/ SA Identity Number"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="text"
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={
              formik.touched.username &&
              (formik.errors.username as React.ReactNode)
            }
          />
        </Grid>

        {isEmail && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Enter Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="password"
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                formik.touched.password &&
                (formik.errors.password as React.ReactNode)
              }
            />
          </Grid>
        )}

        <Grid item xs={12} mt={-2}>
          <OtpVerificationProcess
            expiryDate={expiryDate}
            identityNumber={formik.values.username}
            setExpiryDate={setExpiryDate}
            formikSendCode={formikSendCode}
          />
        </Grid>

        <Grid item xs={12}>
          <LoadingButton
            color="primary"
            variant="contained"
            size="small"
            fullWidth
            startIcon={<LoginIcon />}
            onClick={() =>
              expiryDate && isIdNumber
                ? formikSendCode.submitForm()
                : handleLoginButton()
            }
            loading={loginSelect.loginState.status === "loading"}
            loadingPosition="start"
          >
            {expiryDate && isIdNumber ? "Verify and Login" : "Login"}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
